import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Footer from '../components/common/Footer';
import PinVerification from '../components/gallery/PinVerification';
import GalleryView from '../components/gallery/GalleryView';
import { getRegistrationDetails } from '../services/api';
import { isAuthenticated, getUuid, logout } from '../services/auth';
import { applyGalleryTheme } from '../utils/applyGalleryTheme';

/** Returns #ffffff or #111111 based on background luminance */
const getContrastColor = (hexColor) => {
    if (!hexColor || hexColor.length < 7) return '#ffffff';
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    const toLinear = c => c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return luminance > 0.179 ? '#111111' : '#ffffff';
};

const AIDelivery = () => {
    const { uuid } = useParams();
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    const [themeColor, setThemeColor] = useState('#4CAF50');
    const [galleryTheme, setGalleryTheme] = useState(null);
    const [coverImages, setCoverImages] = useState({ desktop: '', mobile: '' });

    if(uuid !== getUuid()){
        logout();
    }

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                setLoading(true);
                const response = await getRegistrationDetails(uuid);

                if (response.result) {
                    setUserData({
                        name: response.data.name,
                        registrationId: uuid,
                        event: response.data.event || {}
                    });
                    setCoverImages({
                        desktop: response.data.event?.cover_image || '',
                        mobile: response.data.event?.cover_image_mobile || ''
                    });
                    setIsVerified(isAuthenticated());

                    if (response.data.gallery_theme) {
                        applyGalleryTheme(response.data.gallery_theme);
                        setGalleryTheme(response.data.gallery_theme);
                        if (response.data.gallery_theme.color_primary) {
                            setThemeColor(response.data.gallery_theme.color_primary);
                        }
                    }
                } else {
                    setError('Registration not found');
                }
            } catch (err) {
                console.log(err)
                setError('Failed to load registration data');
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [uuid]);

    const handlePinSuccess = () => setIsVerified(true);

    const scrollToContent = () => {
        document.getElementById('ai-delivery-content')?.scrollIntoView({ behavior: 'smooth' });
    };

    if (loading) return <div className="pageloader"><div className="spinner"></div><p>Loading...</p></div>;

    if (error || !userData) {
        return (
            <>
                <div className="container">
                    <div className="error-content mt-4">
                        <p>{error || 'Registration not found'}</p>
                        <button className="button" onClick={() => window.location.reload()}>Try Again</button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    const bgColor = galleryTheme?.color_background || '#000000';
    const textColor = getContrastColor(bgColor);
    const fallbackBg = `linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, ${themeColor} 100%)`;

    return (
        <div className="ai-delivery-page">
            <div
                className="hero hero-fullscreen"
                style={{
                    '--hero-bg-desktop': coverImages.desktop ? `url('${coverImages.desktop}')` : fallbackBg,
                    '--hero-bg-mobile': coverImages.mobile ? `url('${coverImages.mobile}')` : (coverImages.desktop ? `url('${coverImages.desktop}')` : fallbackBg),
                }}
            >
                <div className="hero-overlay"></div>
                <div
                    className="hero-bottom-gradient"
                    style={{ background: `linear-gradient(to top, ${bgColor}f2 0%, ${bgColor}99 45%, transparent 100%)` }}
                />
                <div className="hero-content hero-content-bottom" style={{ color: textColor }}>
                    <p className="hero-label">AI-Powered Photo Delivery</p>
                    <h1 className="hero-title">{userData.event.title || 'Your Photos'}</h1>
                    <button className="hero-view-btn" onClick={scrollToContent}>
                        View Photos
                    </button>
                    <div className="hero-scroll-indicator" onClick={scrollToContent}>
                        <div className="scroll-chevron"></div>
                    </div>
                </div>
            </div>

            <div className="container" id="ai-delivery-content">
                <div className="card mt-4">
                    {!isVerified ? (
                        <PinVerification
                            uuid={uuid}
                            userName={userData.name}
                            onSuccess={handlePinSuccess}
                        />
                    ) : (
                        <GalleryView
                            uuid={uuid}
                            eventDetails={userData.event}
                            lazyLoad={true}
                        />
                    )}
                </div>
            </div>
            <Footer business={userData?.event?.business} />
        </div>
    );
};

export default AIDelivery;
